/**
 * Timesheet React Query Hooks
 * Custom hooks for fetching and managing timesheet data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import timesheetService from '@/api/services/timesheet.service';
import type {
  DailySummariesParams,
  WeeklySummariesParams,
  MonthlySummariesParams,
  QuickStatsParams,
  TrendChartParams,
  ProjectsChartParams,
  UsersChartParams,
  ExportParams,
  ComparePeriodParams,
  YearOverYearParams,
} from '@/api/types/timesheet.types';

// ============================================================================
// Dashboard
// ============================================================================

export const useTimesheetDashboard = () => {
  return useQuery({
    queryKey: ['timesheet', 'dashboard'],
    queryFn: timesheetService.getDashboard,
    staleTime: 5 * 60 * 1000, // 5 min
  });
};

export const useQuickStats = (params: QuickStatsParams) => {
  return useQuery({
    queryKey: ['timesheet', 'quick-stats', params],
    queryFn: () => timesheetService.getQuickStats(params),
    staleTime: 5 * 60 * 1000,
  });
};

// ============================================================================
// Daily Summaries
// ============================================================================

export const useDailySummaries = (params?: DailySummariesParams) => {
  return useQuery({
    queryKey: ['timesheet', 'daily', params],
    queryFn: () => timesheetService.getDailySummaries(params),
    staleTime: 2 * 60 * 1000, // 2 min - shorter since it's real-time data
    enabled: !!params, // Only fetch if params are provided
  });
};

export const useDailySummary = (projectId: string, date: string) => {
  return useQuery({
    queryKey: ['timesheet', 'daily', projectId, date],
    queryFn: () => timesheetService.getDailySummary(projectId, date),
    staleTime: 2 * 60 * 1000,
    enabled: !!projectId && !!date,
  });
};

// ============================================================================
// Weekly Summaries
// ============================================================================

export const useWeeklySummaries = (params?: WeeklySummariesParams) => {
  return useQuery({
    queryKey: ['timesheet', 'weekly', params],
    queryFn: () => timesheetService.getWeeklySummaries(params),
    staleTime: 5 * 60 * 1000,
    enabled: !!params,
  });
};

export const useWeeklySummary = (
  projectId: string,
  year: number,
  week: number
) => {
  return useQuery({
    queryKey: ['timesheet', 'weekly', projectId, year, week],
    queryFn: () => timesheetService.getWeeklySummary(projectId, year, week),
    staleTime: 5 * 60 * 1000,
    enabled: !!projectId && !!year && !!week,
  });
};

// ============================================================================
// Monthly Summaries
// ============================================================================

export const useMonthlySummaries = (params?: MonthlySummariesParams) => {
  return useQuery({
    queryKey: ['timesheet', 'monthly', params],
    queryFn: () => timesheetService.getMonthlySummaries(params),
    staleTime: 10 * 60 * 1000, // 10 min - longer since it changes less frequently
    enabled: !!params,
  });
};

export const useMonthlySummary = (
  projectId: string,
  year: number,
  month: number
) => {
  return useQuery({
    queryKey: ['timesheet', 'monthly', projectId, year, month],
    queryFn: () => timesheetService.getMonthlySummary(projectId, year, month),
    staleTime: 10 * 60 * 1000,
    enabled: !!projectId && !!year && !!month,
  });
};

// ============================================================================
// Charts
// ============================================================================

export const useTrendChart = (params: TrendChartParams) => {
  return useQuery({
    queryKey: ['timesheet', 'chart', 'trend', params],
    queryFn: () => timesheetService.getTrendChart(params),
    staleTime: 10 * 60 * 1000, // 10 min - chart data doesn't change often
    enabled: !!params.period && !!params.metric,
  });
};

export const useProjectsChart = (params: ProjectsChartParams) => {
  return useQuery({
    queryKey: ['timesheet', 'chart', 'projects', params],
    queryFn: () => timesheetService.getProjectsChart(params),
    staleTime: 10 * 60 * 1000,
    enabled: !!params.period && !!params.metric,
  });
};

export const useUsersChart = (params: UsersChartParams) => {
  return useQuery({
    queryKey: ['timesheet', 'chart', 'users', params],
    queryFn: () => timesheetService.getUsersChart(params),
    staleTime: 10 * 60 * 1000,
    enabled: !!params.period,
  });
};

// ============================================================================
// Comparison
// ============================================================================

export const usePeriodComparison = (params: ComparePeriodParams) => {
  return useQuery({
    queryKey: ['timesheet', 'comparison', params.period],
    queryFn: () => timesheetService.comparePeriods(params),
    staleTime: 5 * 60 * 1000,
    enabled: !!params.period,
  });
};

export const useYearOverYearComparison = (params?: YearOverYearParams) => {
  return useQuery({
    queryKey: ['timesheet', 'comparison', 'year-over-year', params],
    queryFn: () => timesheetService.compareYearOverYear(params),
    staleTime: 10 * 60 * 1000,
  });
};

// ============================================================================
// Export (Mutation)
// ============================================================================

export const useExportTimesheet = () => {
  return useMutation({
    mutationFn: async (params: ExportParams & { format: 'csv' | 'excel' }) => {
      const { format, ...exportParams } = params;

      const blob =
        format === 'csv'
          ? await timesheetService.exportCSV(exportParams)
          : await timesheetService.exportExcel(exportParams);

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheet_${exportParams.period}_${exportParams.year}${exportParams.month ? `-${exportParams.month}` : ''}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return blob;
    },
  });
};

// ============================================================================
// Invalidate Queries (for refresh)
// ============================================================================

export const useInvalidateTimesheetQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
    invalidateDashboard: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheet', 'dashboard'] });
    },
    invalidateDaily: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheet', 'daily'] });
    },
    invalidateWeekly: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheet', 'weekly'] });
    },
    invalidateMonthly: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheet', 'monthly'] });
    },
    invalidateCharts: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheet', 'chart'] });
    },
  };
};
